import React from 'react';

export default function RepositoriesExplanation() {
    const [selectedSection, setSelectedSection] = React.useState('definition');

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h2 className="text-2xl font-bold mb-4">Repositories</h2>
                    
                    <div className="mb-4 flex space-x-2">
                        {['definition', 'structure', 'examples', 'implementation'].map(section => (
                            <button
                                key={section}
                                onClick={() => setSelectedSection(section)}
                                className={`px-4 py-2 rounded ${
                                    selectedSection === section
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100'
                                }`}
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </button>
                        ))}
                    </div>

                    {selectedSection === 'definition' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Qu'est-ce qu'un Repository ?</h3>
                            <ul className="space-y-2 list-disc list-inside text-gray-700">
                                <li>Abstraction de la persistance des objets du domaine</li>
                                <li>Collection d'objets en mémoire</li>
                                <li>Interface entre le domaine et la persistance</li>
                                <li>Encapsule la logique de requêtage</li>
                                <li>Assure la cohérence des données</li>
                            </ul>
                        </div>
                    )}

                    {selectedSection === 'structure' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Structure Type</h3>
                            <pre className="bg-gray-50 p-4 rounded text-sm">
{`// 1. Interface du Repository (Port)
public interface PaymentRepository {
    Payment save(Payment payment);
    Optional<Payment> findById(UUID id);
    List<Payment> findByStatus(PaymentStatus status);
    void delete(UUID id);
    
    // Méthodes de recherche spécifiques
    List<Payment> findByDateRange(
        LocalDate start, 
        LocalDate end
    );
    
    Optional<Payment> findByReference(String ref);
}

// 2. Implémentation JPA (Adapter)
@Repository
@Transactional(readOnly = true)
public class JpaPaymentRepository implements PaymentRepository {
    private final PaymentJpaRepository jpaRepository;
    private final PaymentMapper mapper;

    @Override
    @Transactional
    public Payment save(Payment payment) {
        PaymentEntity entity = mapper.toEntity(payment);
        entity = jpaRepository.save(entity);
        return mapper.toDomain(entity);
    }

    @Override
    public Optional<Payment> findById(UUID id) {
        return jpaRepository.findById(id)
            .map(mapper::toDomain);
    }

    @Override
    public List<Payment> findByStatus(PaymentStatus status) {
        return jpaRepository
            .findByStatus(status.name())
            .stream()
            .map(mapper::toDomain)
            .collect(Collectors.toList());
    }
}`}
                            </pre>
                        </div>
                    )}

                    {selectedSection === 'examples' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Exemples de Repositories</h3>
                            
                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">Repository avec Spécifications</h4>
                                <pre className="text-xs mt-2">
{`public interface PaymentRepository {
    List<Payment> findBySpec(PaymentSpecification spec);
}

@Repository
public class JpaPaymentRepository implements PaymentRepository {
    @Override
    public List<Payment> findBySpec(PaymentSpecification spec) {
        CriteriaBuilder cb = em.getCriteriaBuilder();
        CriteriaQuery<PaymentEntity> query = 
            cb.createQuery(PaymentEntity.class);
        Root<PaymentEntity> root = query.from(PaymentEntity.class);
        
        // Application des spécifications
        Predicate predicate = spec.toPredicate(root, cb);
        query.where(predicate);
        
        // Exécution et mapping
        return em.createQuery(query)
            .getResultStream()
            .map(mapper::toDomain)
            .collect(Collectors.toList());
    }
}

// Utilisation
public class PaymentByStatusSpec implements PaymentSpecification {
    private final PaymentStatus status;
    
    @Override
    public Predicate toPredicate(
        Root<PaymentEntity> root, 
        CriteriaBuilder cb
    ) {
        return cb.equal(
            root.get("status"), 
            status.name()
        );
    }
}`}
                                </pre>
                            </div>

                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">Repository avec Cache</h4>
                                <pre className="text-xs mt-2">
{`@Repository
public class CachedPaymentRepository implements PaymentRepository {
    private final PaymentRepository delegate;
    private final CacheManager cacheManager;

    @Override
    public Optional<Payment> findById(UUID id) {
        // Vérification du cache
        Cache cache = cacheManager.getCache("payments");
        Payment cached = cache.get(id, Payment.class);
        
        if (cached != null) {
            return Optional.of(cached);
        }

        // Récupération depuis la base
        return delegate.findById(id)
            .map(payment -> {
                cache.put(id, payment);
                return payment;
            });
    }

    @Override
    @CacheEvict(cacheNames = "payments", key = "#payment.id")
    public Payment save(Payment payment) {
        return delegate.save(payment);
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}

                    {selectedSection === 'implementation' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Patterns d'Implémentation</h3>
                            
                            <div className="space-y-2">
                                <h4 className="font-medium">1. Pattern Optimistic Locking</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Repository
public class OptimisticPaymentRepository 
    implements PaymentRepository {
    
    @Override
    @Transactional
    public Payment save(Payment payment) {
        try {
            return doSave(payment);
        } catch (ObjectOptimisticLockingFailureException e) {
            throw new ConcurrencyException(
                "Payment was modified: " + payment.getId()
            );
        }
    }

    private Payment doSave(Payment payment) {
        PaymentEntity entity = mapper.toEntity(payment);
        entity.setVersion(payment.getVersion());
        entity = jpaRepository.save(entity);
        return mapper.toDomain(entity);
    }
}`}
                                </pre>
                            </div>

                            <div className="space-y-2">
                                <h4 className="font-medium">2. Pattern Query Object</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`public class PaymentQuery {
    private PaymentStatus status;
    private LocalDate fromDate;
    private LocalDate toDate;
    private Integer page;
    private Integer size;
    
    public Specification<PaymentEntity> toSpecification() {
        return Specification.where(statusEquals())
            .and(datesBetween());
    }
    
    private Specification<PaymentEntity> statusEquals() {
        return status == null ? null :
            (root, query, cb) -> 
                cb.equal(root.get("status"), status);
    }
}

@Repository
public class QueryablePaymentRepository 
    implements PaymentRepository {
    
    public Page<Payment> findByQuery(PaymentQuery query) {
        Pageable pageable = PageRequest.of(
            query.getPage(), 
            query.getSize()
        );
        
        return jpaRepository
            .findAll(query.toSpecification(), pageable)
            .map(mapper::toDomain);
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg p-4 shadow">
                    <h3 className="text-xl font-bold mb-4">Bonnes Pratiques</h3>
                    
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 pl-4">
                            <h4 className="font-semibold">1. Single Responsibility</h4>
                            <p className="text-sm text-gray-600">
                                Un repository par agrégat racine
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                PaymentRepository, MandateRepository
                            </code>
                        </div>

                        <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold">2. Interface Ségrégation</h4>
                            <p className="text-sm text-gray-600">
                                Séparer les interfaces selon les besoins
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                ReadOnlyRepository, WriteOnlyRepository
                            </code>
                        </div>

                        <div className="border-l-4 border-purple-500 pl-4">
                            <h4 className="font-semibold">3. Gestion des Transactions</h4>
                            <p className="text-sm text-gray-600">
                                Déléguer la gestion des transactions à la couche service
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                @Transactional au niveau service
                            </code>
                        </div>

                        <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold">4. Mapping Explicite</h4>
                            <p className="text-sm text-gray-600">
                                Mapper explicitement entre domaine et persistance
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                Mappers dédiés, pas d'entités anémiques
                            </code>
                        </div>

                        <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold">5. Performance</h4>
                            <p className="text-sm text-gray-600">
                                Optimiser les requêtes et utiliser le cache
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                Cache, Batch operations, Pagination
                            </code>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}